import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useDebounce } from "../../../../hooks/useDebounce";
import { getUsers } from "../../../../store/user/useCases/getUsers/action";
import { selectUser } from "../../../../store/user/repository/selector";
import { tokens } from "../../../../locales/tokens";
import { TIMESTAMP_DAY } from "../../../../config";
import toast from "react-hot-toast";
import { setUpdateQuestionError } from "../../../../store/generalQuestion/repository/slice";
import { IAutocompleteItem, IFilters } from "./types";
import { selectIsIdea } from "../../../../store/isIdea/repository/selector";
import { getIdeaList } from "../../../../store/isIdea/useCases/getIdeaList/action";
import { updateIdea } from "../../../../store/isIdea/useCases/updateIdea/action";

export const useIsIdeaList = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [searchText, setSearchText] = useState('');
    const [filters, updateFilters] = useState<IFilters>({ page: 1, rowsPerPage: 30, search: '', status: null, date: null });
    const { userList } = useSelector(selectUser);
    const [pageAmount, setPageAmount] = useState(0);
    const { generalQuestionList, generalQuestionCount, isLoading, getQuestionsError, updateQuestionError } = useSelector(selectIsIdea);
    const [updateClick, setUpdateClick] = useState(false);
    const statusOptions: IAutocompleteItem[] = [
        { id: 1, name: t(tokens.feedback.inWork), value: "inWork" },
        { id: 2, name: t(tokens.feedback.takeToDevelop), value: "takeToDevelop" },
        { id: 3, name: t(tokens.feedback.refusal), value: "refusal" },
        { id: 4, name: t(tokens.feedback.duplicate), value: "duplicate" },
        { id: 5, name: t(tokens.feedback.pause), value: "pause" },
        { id: 6, name: t(tokens.feedback.notEnoughInformation), value: "notEnoughInformation" },
        { id: 7, name: t(tokens.feedback.embodied), value: "embodied" },
    ];
    const [isProccessQuestion, setProcessQuestion] = useState(false);
    const [isOpenImageFile, setOpenImageFile] = useState(false);
    const [currentImage, setCurrentImge] = useState<any>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        dispatch(getIdeaList(getRequestBody()));
    }, [filters]);

    useEffect(() => {
        dispatch(getUsers({ page: 1, perPage: 30 }));
    }, []);

    useEffect(() => {
        if (generalQuestionCount) {
            setPageAmount(Math.ceil(generalQuestionCount / filters.rowsPerPage));
        }
    }, [generalQuestionCount]);

    useEffect(() => {
        if (!isLoading && isProccessQuestion) {
            if (getQuestionsError) {
                toast.error(getQuestionsError)
            };
            setTimeout(() => setProcessQuestion(false), 400);
        }
    }, [getQuestionsError, isLoading]);

    useEffect(() => {
        if (!isLoading && updateClick) {
            if (updateQuestionError) {
                toast.error(updateQuestionError)
                dispatch(setUpdateQuestionError(null));
            } else {
                toast.success(t(tokens.feedback.message.ideaUpdated));
                dispatch(getIdeaList(getRequestBody()));
                dispatch(setUpdateQuestionError(null));
            }
            setUpdateClick(false);
        }
    }, [updateQuestionError, isLoading]);

    const getRequestBody = () => {
        const body: any = { page: filters.page, perPage: filters.rowsPerPage };
        if (filters.search.length) body.search = filters.search;
        if (filters.status) body.status = filters.status.value;
        if (filters.date) body.date = new Date(filters.date + TIMESTAMP_DAY).toISOString();
        return body;
    };

    const scrollTopList = useCallback(() => {
        if (scrollRef.current) {
            // scrollRef.current.scrollTop = 0;
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }, [scrollRef]);

    const handlePageChange = useCallback((event: ChangeEvent<unknown>, page: number): void => {
        updateFilters((prevState) => ({ ...prevState, page }));
        scrollTopList();
    }, [updateFilters]);

    const handleSearchUserText = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
        debouncedHandleUserSeacrh(e.target.value);
    };

    const onSearchUser = (value: string) => {
        const query = value ? value : '';
        if (query.length !== 1) {
            updateFilters((prevState) => ({ ...prevState, search: query, page: 1 }));
        };
    };

    const { debouncedWrapper: debouncedHandleUserSeacrh } = useDebounce(onSearchUser, 1000);

    const resetFilters = useCallback((): void => {
        updateFilters((prevState) => ({ ...prevState, page: 1, status: null, date: null }));
    }, [updateFilters]);

    const handleDateChange = useCallback((date: Date | null): void => {
        const newDate = date?.valueOf();
        const currentDate = newDate || null;
        updateFilters((prevState) => ({ ...prevState, page: 1, date: currentDate }));
    }, [updateFilters]);

    const changeStatus = useCallback((status: IAutocompleteItem) => {
        updateFilters((prevState) => ({ ...prevState, page: 1, status }));
    }, [updateFilters]);

    const onUserChange = (value: IAutocompleteItem, questionId: number) => {
        const id = value ? value.id : null;
        dispatch(updateIdea({ id: questionId, responsibleId: id }));
        setUpdateClick(true);
    };

    const changeUserText = (event: ChangeEvent<HTMLInputElement>) => {
        debouncedHandleUsersSelect(event.target.value);
    };

    const onSearch = (value: string) => {
        if (value === '') {
            dispatch(getUsers({ page: 1, perPage: 30 }));
            return;
        } else {
            dispatch(getUsers({ page: 1, perPage: 30, name: value }));
        };
    };

    const { debouncedWrapper: debouncedHandleUsersSelect } = useDebounce(onSearch, 1000);

    const changeQuestionStatus = (status: IAutocompleteItem, questionId: number) => {
        const statusValue = status ? status.value : null;
        dispatch(updateIdea({ id: questionId, status: statusValue }));
        setUpdateClick(true);
    };

    const handleToggleImageFile = (image: any) => {
        setOpenImageFile(!isOpenImageFile);
        setCurrentImge(image);
    };

    return {
        searchText, handleSearchUserText, filters, resetFilters, changeStatus, handleDateChange, generalQuestionList, changeUserText, currentImage, scrollRef,
        onUserChange, userList, changeQuestionStatus, statusOptions, pageAmount, handlePageChange, isProccessQuestion, isOpenImageFile, handleToggleImageFile
    }
};